import React, { useState, useEffect } from 'react';
import { collection, getDocs, updateDoc, doc, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { FaUser, FaUserFriends, FaIdCard, FaPhone, FaEnvelope } from 'react-icons/fa';

const DriverAssignmentModal = ({ vehicle, onClose, onAssignmentUpdated }) => {
  const [drivers, setDrivers] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(vehicle?.driver || '');
  const [selectedAssistant, setSelectedAssistant] = useState(vehicle?.assistant || '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch drivers (users with role 'driver' and status 'active')
      const driversRef = collection(db, 'users');
      const driversQuery = query(
        driversRef,
        where('role', '==', 'driver'),
        where('status', '==', 'active')
      );
      const driversSnapshot = await getDocs(driversQuery);
      const driversList = driversSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setDrivers(driversList);

      // Fetch assistants (users with role 'bus_assistant' and status 'active')
      const assistantsQuery = query(
        driversRef,
        where('role', '==', 'bus_assistant'),
        where('status', '==', 'active')
      );
      const assistantsSnapshot = await getDocs(assistantsQuery);
      const assistantsList = assistantsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAssistants(assistantsList);
    } catch (error) {
      console.error('Error fetching staff:', error);
      setError('Failed to fetch staff members');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const selectedDriverData = drivers.find(d => d.id === selectedDriver);
      const selectedAssistantData = assistants.find(a => a.id === selectedAssistant);

      const vehicleRef = doc(db, 'vehicles', vehicle.id);
      await updateDoc(vehicleRef, {
        assignedDriver: selectedDriverData ? {
          id: selectedDriverData.id,
          name: `${selectedDriverData.firstName} ${selectedDriverData.lastName}`,
          phone: selectedDriverData.phoneNumber,
          license: selectedDriverData.driversLicense,
          profilePicture: selectedDriverData.profilePicture || null
        } : null,
        assignedAssistant: selectedAssistantData ? {
          id: selectedAssistantData.id,
          name: `${selectedAssistantData.firstName} ${selectedAssistantData.lastName}`,
          phone: selectedAssistantData.phoneNumber
        } : null,
        lastAssignmentUpdate: new Date().toISOString()
      });

      if (onAssignmentUpdated) {
        onAssignmentUpdated();
      }
      onClose();
    } catch (error) {
      console.error('Error updating assignments');
      setError('Failed to update assignments');
    }
  };

  const renderUserInfo = (user) => (
    <div className="text-sm text-gray-600">
      <p className="flex items-center gap-1">
        <FaIdCard className="text-gray-400" />
        {user.nationalId}
      </p>
      <p className="flex items-center gap-1">
        <FaPhone className="text-gray-400" />
        {user.phoneNumber}
      </p>
      <p className="flex items-center gap-1">
        <FaEnvelope className="text-gray-400" />
        {user.email}
      </p>
      {user.driversLicense && (
        <p className="flex items-center gap-1">
          <FaIdCard className="text-gray-400" />
          License: {user.driversLicense}
        </p>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
        <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
          <div className="text-center">Loading staff members...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-black">Assign Staff - {vehicle?.registrationNumber}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700" aria-label="Close modal">×</button>
          </div>
          {error && (
            <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg mb-6">{error}</div>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2"><FaUser className="inline mr-2" />Driver</label>
              <select value={selectedDriver} onChange={(e) => setSelectedDriver(e.target.value)} className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]">
                <option value="">Select Driver</option>
                {drivers.map((driver) => (
                  <option key={driver.id} value={driver.id}>{driver.firstName} {driver.lastName} - {driver.driversLicense}</option>
                ))}
              </select>
              {selectedDriver && (
                <div className="mt-2 p-3 bg-gray-50 rounded">
                  {renderUserInfo(drivers.find(d => d.id === selectedDriver))}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2"><FaUserFriends className="inline mr-2" />Bus Assistant</label>
              <select value={selectedAssistant} onChange={(e) => setSelectedAssistant(e.target.value)} className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]">
                <option value="">Select Assistant</option>
                {assistants.map((assistant) => (
                  <option key={assistant.id} value={assistant.id}>{assistant.firstName} {assistant.lastName}</option>
                ))}
              </select>
              {selectedAssistant && (
                <div className="mt-2 p-3 bg-gray-50 rounded">
                  {renderUserInfo(assistants.find(a => a.id === selectedAssistant))}
                </div>
              )}
            </div>
            <div className="flex justify-end space-x-4 mt-6">
              <button type="button" onClick={onClose} className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors">Cancel</button>
              <button type="submit" className="flex items-center space-x-2 px-6 py-3 rounded-lg bg-[#00E0E0] text-white font-semibold hover:bg-[#00B8B8] transition-colors"><FaUserFriends /><span>Save Assignments</span></button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DriverAssignmentModal; 